import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from './translations';
import { contactInfo } from './data/products';
import ProductCatalog from './components/ProductCatalog';
import TowelDryers from './components/TowelDryers';
import UsageGallery from './components/UsageGallery';
import ImageGallery from './components/ImageGallery';
import './styles/App.css';

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Detect language from URL path
  const lang = location.pathname.startsWith('/ru') ? 'ru' : 'it';
  const t = translations[lang];

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [initialImageIndex, setInitialImageIndex] = useState(0);
  const [warrantyExpanded, setWarrantyExpanded] = useState(false);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const toggleLanguage = () => {
    if (lang === 'it') {
      navigate('/ru');
    } else {
      navigate('/');
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleProductClick = (product, startIndex = 0) => {
    setSelectedProduct(product);
    setInitialImageIndex(startIndex);
    setShowGallery(true);
  };

  const handleCloseGallery = () => {
    setShowGallery(false);
    setSelectedProduct(null);
    setInitialImageIndex(0);
  };

  const toggleWarranty = () => {
    setWarrantyExpanded(!warrantyExpanded);
  };

  // Handle warranty link click from footer
  useEffect(() => {
    if (location.hash === '#warranty') {
      setWarrantyExpanded(true);
      // Scroll to warranty section after a small delay
      setTimeout(() => {
        const warrantySection = document.getElementById('warranty');
        if (warrantySection) {
          warrantySection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">SSE | GPO</div>
          <nav className="nav">
            <a href="#products">{t.products}</a>
            <a href="#features">{t.features}</a>
            <a href="#contact">{t.contact}</a>
            <button className="lang-switch" onClick={toggleLanguage}>
              {t.switchLang}
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <h1>{t.heroTitle}</h1>
          <p>{t.heroSubtitle}</p>
          <button
            className="cta-button"
            onClick={() => handleCall(contactInfo.scalea.phone)}
          >
            {t.heroButton}
          </button>
        </div>
      </section>

      {/* Products Section */}
      <ProductCatalog onProductClick={handleProductClick} lang={lang} />
      <TowelDryers onProductClick={handleProductClick} lang={lang} />
      <UsageGallery onImageClick={handleProductClick} lang={lang} />

      {/* Technical Specs Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">{t.specsTitle}</h2>
          <div className="tech-specs-grid">
            <div className="tech-spec-card">
              <h3>{t.heatingTech}</h3>
              <p>{t.heatingTechDesc}</p>
            </div>
            <div className="tech-spec-card">
              <h3>{t.glassConstruction}</h3>
              <p>{t.glassConstructionDesc}</p>
            </div>
            <div className="tech-spec-card">
              <h3>{t.smartControl}</h3>
              <p>{t.smartControlDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaSubtitle}</p>
          <button
            className="cta-button"
            onClick={() => handleCall(contactInfo.scalea.phone)}
          >
            {t.ctaButton}
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section">
        <div className="container">
          <h2 className="section-title">{t.featuresTitle}</h2>
          <div className="features-grid">
            <div className="feature-card">
              <h3>💶 {t.feature1Title}</h3>
              <p>{t.feature1Text}</p>
            </div>
            <div className="feature-card">
              <h3>🤫 {t.feature2Title}</h3>
              <p>{t.feature2Text}</p>
            </div>
            <div className="feature-card">
              <h3>⏲️ {t.feature3Title}</h3>
              <p>{t.feature3Text}</p>
            </div>
            <div className="feature-card">
              <h3>🔨 {t.feature4Title}</h3>
              <p>{t.feature4Text}</p>
            </div>
            <div className="feature-card">
              <h3>👨🏻‍🔧 {t.feature5Title}</h3>
              <p>{t.feature5Text}</p>
            </div>
            <div className="feature-card">
              <h3>✨ {t.feature6Title}</h3>
              <p>{t.feature6Text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Section */}
      <section id="delivery" className="section">
        <div className="container">
          <h2 className="section-title">{t.deliveryTitle}</h2>
          <p className="section-subtitle">{t.deliverySubtitle}</p>
          <div className="delivery-content">
            <div className="delivery-card">
              <div className="delivery-icon">📦</div>
              <p className="delivery-description">{t.deliveryDescription}</p>
              <button
                className="cta-button"
                onClick={() => handleCall(contactInfo.scalea.phone)}
              >
                {t.deliveryButton}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Warranty Section */}
      <section id="warranty" className="section">
        <div className="container">
          <div
            className="warranty-header"
            onClick={toggleWarranty}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleWarranty();
              }
            }}
          >
            <div className="warranty-header-content">
              <div>
                <h2 className="section-title">{t.warrantyTitle}</h2>
                <p className="section-subtitle">{t.warrantySubtitle}</p>
              </div>
              <div className={`warranty-toggle-icon ${warrantyExpanded ? 'expanded' : ''}`}>
                {warrantyExpanded ? '▼' : '▶'}
              </div>
            </div>
          </div>

          <div className={`warranty-content ${warrantyExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="warranty-card">
              <div className="warranty-icon">🛡️</div>

              <div className="warranty-section">
                <h3>{t.warrantyTermsTitle}</h3>
                <p>{t.warrantyTermsText}</p>
              </div>

              <div className="warranty-section">
                <h3>{t.warrantyCoverageTitle}</h3>
                <p>{t.warrantyCoverageText}</p>
              </div>

              <div className="warranty-section">
                <h3>{t.warrantyExclusionsTitle}</h3>
                <ul className="warranty-list">
                  {t.warrantyExclusions.map((exclusion, index) => (
                    <li key={index}>{exclusion}</li>
                  ))}
                </ul>
              </div>

              <div className="warranty-section">
                <h3>{t.warrantyClaimsTitle}</h3>
                <p>{t.warrantyClaimsText}</p>
              </div>

              <div className="warranty-section">
                <h3>{t.warrantyLiabilityTitle}</h3>
                <p>{t.warrantyLiabilityText}</p>
              </div>

              <div className="warranty-footer">
                <p className="warranty-date">{t.warrantyDate}</p>
              </div>

              <button
                className="cta-button"
                onClick={() => handleCall(contactInfo.scalea.phone)}
              >
                {t.warrantyButton}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <h2 className="section-title">{t.contactTitle}</h2>
          <div className="contact-grid">
            {/* Scalea Office */}
            <div className="contact-card">
              <h3>{t.scaleaOffice}</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-label">{t.phone}:</span>
                  <a
                    href={`tel:${contactInfo.scalea.phone}`}
                    className="phone-link"
                  >
                    {contactInfo.scalea.phoneDisplay}
                  </a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">{t.address}:</span>
                  <span>
                    {contactInfo.scalea.address}<br />
                    {contactInfo.scalea.city}<br />
                    {contactInfo.scalea.country}
                  </span>
                </div>
                <div className="contact-item">
                  <span className="contact-label">{t.openHours}:</span>
                  <span>
                    {t.weekdays}<br />
                    {t.saturday}
                  </span>
                </div>
              </div>
              <button
                className="cta-button"
                onClick={() => handleCall(contactInfo.scalea.phone)}
              >
                {t.heroButton}
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-section">
            <h3>{t.footerCompany}</h3>
            <p>{t.footerAbout}</p>
          </div>
          <div className="footer-section">
            <h3>{t.footerProducts}</h3>
            <a href="#products">{t.spaceHeaters}</a>
            <a href="#products">{t.towelDryers}</a>
            <a href="#products">{t.shoeDryers}</a>
          </div>
          <div className="footer-section">
            <h3>{t.footerSupport}</h3>
            <a href="#warranty">{t.footerWarranty}</a>
            <a href="#contact">{t.footerContact}</a>
            <a href="#delivery">{t.footerShipping}</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 GPO OÜ. {t.allRightsReserved}</p>
        </div>
      </footer>

      {showGallery && selectedProduct && (
        <ImageGallery
          product={selectedProduct}
          initialIndex={initialImageIndex}
          onClose={handleCloseGallery}
          lang={lang}
        />
      )}
    </div>
  );
}

export default App;
